import { Component, ChangeDetectionStrategy, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BadgeVariant,
  IconComponent,
  ButtonComponent,
  NebulaAccionDirective,
  FormDensityComponent,
} from 'nebula-ui-kit';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FavoritosService, LAYOUT, TranslatePipe, porPerfil } from '@nebula-shared/data-access';

export interface PageHeaderIcon {
  name: string;
  color?: string;
}

export interface PageAction {
  id: string;
  label: string;
  icon?: string;
  /** Clase del boton; `text` es el de solo texto (Cancelar, Volver). */
  variant?: 'primary' | 'secondary' | 'text' | 'danger';
  includeBorder?: boolean;
  disabled?: boolean;
  visible?: boolean;
  /**
   * Llave Flujo 2 para `nebulaAccion` (p. ej. SAVE, REJECT, VIEW).
   * Si no se define, el botón no aplica gate del kit.
   */
  accion?: string;
}

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    ButtonComponent,
    NebulaAccionDirective,
    FormDensityComponent,
    MatTooltipModule,
    TranslatePipe,
  ],
  template: `
    <header class="page-header">
      <div class="header-title">
        <div class="header-text">
          <h1>
            @if (icon()) {
              <lib-icon
                [name]="$any(icon()!.name)"
                class="inline-icon"
                [class.custom-color]="icon()!.color !== undefined"
                [style.color]="icon()!.color"
                aria-hidden="true"
              />
            }
            <span class="header-title-text">{{ title() }}</span>
            <!-- ETIQUETA: el subtitulo junto al titulo (p. ej. «Gasto · Vigencia 2026»), si se pide. -->
            @if (subtitleAsTag() && subtitle()) {
              <span class="header-etiqueta">{{ subtitle() }}</span>
            }
            <!-- PASTILLA: estado vivo de la pantalla junto al titulo (p. ej. «2 tareas en curso»). -->
            @if (pastilla()) {
              <span
                class="header-pastilla"
                [class.header-pastilla--quieta]="!pastillaActiva()"
                [attr.data-variante]="pastillaVariante()"
                role="status"
              >
                @if (pastillaActiva()) {
                  <span class="header-pastilla__punto" aria-hidden="true"></span>
                }
                {{ pastilla() }}
              </span>
            }
            <!--
              FAVORITO: pegada al nombre, la estrella agrega o quita la pantalla del grupo
              Favoritos del menu. Marca la PANTALLA, no actua sobre sus datos: por eso va con el
              nombre y no entre las acciones. Solo la ofrece una pantalla que lo admite.
            -->
            @if (favoritos.ofrecida()) {
              @let marcada = favoritos.actualEsFavorita();
              <button
                type="button"
                class="header-favorito"
                [class.header-favorito--marcada]="marcada"
                [attr.aria-pressed]="marcada"
                [attr.aria-label]="(marcada ? textos.REMOVE : textos.ADD) | translate"
                [matTooltip]="(marcada ? textos.REMOVE : textos.ADD) | translate"
                (click)="favoritos.alternarActual()"
              >
                <lib-icon name="Star" [size]="16"></lib-icon>
              </button>
            }
          </h1>
          @if (subtitle() && !subtitleAsTag()) {
            <p class="subtitle">{{ subtitle() }}</p>
          }
        </div>
      </div>

      <div class="header-actions">
        <!-- EXTREMO: lo que la pantalla pone a la derecha del titulo, antes de sus acciones. -->
        <ng-content select="[appCabeceraExtremo]" />
        @for (action of actions(); track action.id) {
          @if (action.visible !== false) {
            <div class="action-button-wrapper desktop-only">
              @if (action.accion) {
                <lib-button
                  [variant]="variante(action)"
                  [includeBorder]="action.includeBorder"
                  [disabled]="action.disabled || false"
                  [prefixIcon]="$any(action.icon)"
                  [nebulaAccion]="action.accion"
                  (clicked)="actionClick.emit(action.id)"
                >
                  {{ action.label }}
                </lib-button>
              } @else {
                <lib-button
                  [variant]="variante(action)"
                  [includeBorder]="action.includeBorder"
                  [disabled]="action.disabled || false"
                  [prefixIcon]="$any(action.icon)"
                  (clicked)="actionClick.emit(action.id)"
                >
                  {{ action.label }}
                </lib-button>
              }
            </div>

            <div class="action-button-wrapper mobile-only">
              <div class="icon-button-with-tooltip" [attr.data-tooltip]="action.label">
                @if (action.accion) {
                  <lib-button
                    [variant]="variante(action)"
                    [includeBorder]="action.includeBorder"
                    [disabled]="action.disabled || false"
                    [prefixIcon]="$any(action.icon)"
                    [nebulaAccion]="action.accion"
                    (clicked)="actionClick.emit(action.id)"
                    class="icon-only-button"
                  >
                    <span class="sr-only">{{ action.label }}</span>
                  </lib-button>
                } @else {
                  <lib-button
                    [variant]="variante(action)"
                    [includeBorder]="action.includeBorder"
                    [disabled]="action.disabled || false"
                    [prefixIcon]="$any(action.icon)"
                    (clicked)="actionClick.emit(action.id)"
                    class="icon-only-button"
                  >
                    <span class="sr-only">{{ action.label }}</span>
                  </lib-button>
                }
              </div>
            </div>
          }
        }

        <ng-content select="[header-actions]"></ng-content>

        <!--
          La densidad del formulario, siempre la ultima y siempre a la derecha: el sitio
          del canto es suyo, y lo que se anada va delante. Solo aparece cuando la pantalla
          tiene un formulario del motor debajo; en las demas no se pinta.
        -->
        <lib-form-density class="header-density"></lib-form-density>
      </div>
    </header>
  `,
  styleUrls: ['./page-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHeaderComponent {
  title = input.required<string>();
  subtitle = input<string>();
  /** El subtitulo se pinta como etiqueta junto al titulo, no debajo (mockup «Crear Rubro»). */
  subtitleAsTag = input<boolean>(false);
  /** Pastilla con punto pulsante junto al titulo; sin valor, no se pinta. */
  pastilla = input<string | null>(null);
  /** Pastilla viva (tono primario y punto pulsante) o quieta (neutra, sin punto). */
  pastillaActiva = input<boolean>(true);
  /**
   * Color de la pastilla segun el estado que nombra, con las variantes de `lib-badge` (p. ej. el
   * estado de un documento: Borrador neutral, Aprobado success). Solo tiñe en el perfil 3.0; sin
   * valor, la pastilla queda como siempre.
   */
  pastillaVariante = input<BadgeVariant | null>(null);
  icon = input<PageHeaderIcon>();
  actions = input<PageAction[]>([]);
  /**
   * Si la pantalla pinta el motor de formulario dinamico. Solo entonces lo secundario va como
   * solo texto en el perfil 3.0; un listado conserva su secundario (orden del arquitecto, 09-oct).
   */
  motorFormulario = input<boolean>(false);
  actionClick = output<string>();

  protected readonly favoritos = inject(FavoritosService);
  protected readonly textos = LAYOUT.FAVORITES;

  /**
   * Como se pinta un boton de la cabecera. En una pantalla del motor de formulario dinamico y en
   * el perfil 3.0, lo secundario va como solo texto, que se rellena al pasar el puntero o con el
   * foco -como el Cancelar-; el principal no cambia (orden del arquitecto, 09-oct-2026). Los
   * listados y los demas perfiles conservan el secundario de siempre.
   *
   * @param action Accion de la cabecera.
   * @returns La variante del boton.
   */
  protected variante(action: PageAction): NonNullable<PageAction['variant']> {
    const variante = action.variant || 'secondary';
    if (!this.motorFormulario() || variante !== 'secondary') return variante;
    return porPerfil<'text' | 'secondary'>('text', 'secondary');
  }
}
